import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

// Import Layout, Loader, and static components
import AppLayout from "./AppLayout";
import Loader from "./Loader";
import NoAuth from "./NoAuth";
import Login from "./Login";
import ResetPasswordForm from "./ResetPassword";
import NewPasswordForm from "./NewPasswordForm";

// Helper for permissions (from HomeScreen.js)
const CheckPermission = ({ children, path }) => {
  const rolesArray = localStorage.getItem("rolesArray");
  const checkList = rolesArray ? rolesArray.split(",") : [];
  return checkList.includes(path) ? children : <NoAuth />;
};

// Components (Lazy Loaded)
const EnquiryCategories = lazy(() => import("./EnquiryCategories"));
const Branch = lazy(() => import("./Branch"));
const State_list = lazy(() => import("./Master/State/State_list"));
const District_list = lazy(() => import("./Master/District/District_list"));
const Village_list = lazy(() => import("./Master/Village/Village_list"));
const Taluka_list = lazy(() => import("./Master/Taluka/Taluka_list"));
const Part_List = lazy(() => import("./Master/Plan/Part_List"));
const BreadCrumb = lazy(() => import("./BreadCrumb/BreadCrumb"));
const Manufacturer_list = lazy(() =>
  import("./Master/Manufacturer/Manufacturer_list")
);
const Manufacturer_modal = lazy(() =>
  import("./Master/Manufacturer/Manufacturer_modal")
);
const Department_list = lazy(() =>
  import("./Master/Department/Department_list")
);
const Category_list = lazy(() => import("./Master/Category/Category_list"));
const Profile_list = lazy(() => import("./Profile"));
const Roles = lazy(() => import("./Roles"));
const Tax = lazy(() => import("./Master/Tax/Tax"));
const Variants = lazy(() => import("./Master/Manufacturer/Sub_modal"));
const Dashboard = lazy(() => import("./Dashboard"));
const UserProfile = lazy(() => import("./UserProfile"));
const Report = lazy(() => import("./Report"));
const Task = lazy(() => import("./Task"));
const UserTaskDetails = lazy(() => import("./UserTaskDetails"));
const EnquirySources = lazy(() =>
  import("./Master/EnquirySources/EnquirySources")
);
const Holiday = lazy(() => import("./Master/Holiday/Holiday"));
const Message = lazy(() => import("./Master/Message/Message"));
const EnquirySources_model = lazy(() =>
  import("./Master/EnquirySources/EnquirySources_modal")
);
const WorkReport = lazy(() => import("./WorkReport"));
const WorkReportDetails = lazy(() => import("./WorkReportDetails"));
const MyLeave = lazy(() => import("./MyLeave"));
const MessagesList = lazy(() => import("./Master/Message/MessagesList"));
const SendMessages = lazy(() => import("./SendMessages"));
const CustomField = lazy(() => import("./Master/CoustoField/CustomField"));
const CallReport = lazy(() => import("./CallReport"));
const CallReportInDetails = lazy(() => import("./CallReportInDetails"));
const TrackReport = lazy(() => import("./TrackReport"));
const ReportNotification = lazy(() =>
  import("./Master/notification/ReportNotification")
);
const LeadsBoard = lazy(() => import("./Leads"));
const EnquiryStage = lazy(() => import("./report/enquiry/LostEnquiry"));
const EmployeeForm = lazy(() => import("./EmployeeForm"));
const SalesData = lazy(() => import("./sale/data/SalesData"));
const EditEnquiry = lazy(() => import("./stages/EditEnquiry"));
const EnquiryBoard = lazy(() => import("./sale/enquiry/EnquiryBoard"));
const AddData = lazy(() => import("./sale/data/AddData"));
const SalesReport = lazy(() => import("./report/enquiry/SalesReport"));
const TaskForm = lazy(() => import("./TaskForm"));
const TaskCompletionReport = lazy(() => import("./TaskCompletionReport"));
const ReportMenu = lazy(() => import("./report/ReportMenu"));
const TaskAnalyticsReport = lazy(() => import("./TaskAnalyticsReport"));
const ServicesMenu = lazy(() => import("./servicing/ServicesMenu"));
const AssociatedServices = lazy(() => import("./servicing/AssociatedServices"));
const AddRecordForm = lazy(() => import("./inventory/AddRecordForm"));
const ModelStock = lazy(() => import("./inventory/ModelStock"));
const ProductsList = lazy(() => import("./Products"));
const DeliveryProcess = lazy(() => import("./delivery/DeliveryProcess"));
// const Login = lazy(() => import("./Login"));
const DeliveryStepsData = lazy(() => import("./delivery/DeliveryStepsData"));
const DeliveriesData = lazy(() => import("./delivery/DeliveriesData"));
const AdditionalRecordformWrapper = lazy(() =>
  import("./delivery/AdditionalForm")
);
const DeliveryForm = lazy(() => import("./stages/DeliveryForm"));
const AdditionalForm = lazy(() => import("./delivery/AdditionalForm"));
const ServicesDue = lazy(() => import("./servicing/ServicesDue"));
const EmployeeTrackingDashboard = lazy(() =>
  import("./tracking/EmployeeTrackingDashboard")
);
const AllServicesData = lazy(() => import("./servicing/AllServicesData"));
const TractorSoldOut = lazy(() => import("./servicing/TractorSoldOut"));
const Offers = lazy(() => import("./Offers"));
const LeadsRecords = lazy(() => import("./report/enquiry/LeadsRecord"));
const OrderRecord = lazy(() => import("./production/OrderRecord"));
const LeadsData = lazy(() => import("./sale/data/LeadsData"));
const FieldMapping = lazy(() => import("./manager/FieldMapping"));
const Config = lazy(() => import("./Master/Config/Config"));
const ConfigList = lazy(() => import("./Master/Config/ConfigList"));
const EditConfig = lazy(() => import("./Master/Config/EditConfig"));
const ServiceRecord = lazy(() => import("./service/ServiceRecord"));

// Newly added ones
const AddRole = lazy(() => import("./AddRole"));
// const NoAuth = lazy(() => import("./NoAuth"));
const EnquiryList = lazy(() => import("./EnquiryList"));
const AddDepartment = lazy(() => import("./AddDepartment"));
const AddCategory = lazy(() => import("./AddCategory"));
const AddBank = lazy(() => import("./AddBank"));
const AddOffer = lazy(() => import("./AddOffer"));
const Sales = lazy(() => import("./Sales"));
const Manage = lazy(() => import("./Manage"));
const Profile = lazy(() => import("./Profile"));
const Enquiry = lazy(() => import("./Enquiry"));
const ModelBillings = lazy(() => import("./inventory/ModelBillings"));
const Master = lazy(() => import("./Master"));
const Employees = lazy(() => import("./Employees"));
const AreaAssignListList = lazy(() => import("./AreaAssignListList"));
const AddAssignArea = lazy(() => import("./AddAssignArea"));
const Work_Assign_Area = lazy(() =>
  import("./Master/Work Assign/Work_Assign_Area")
);
const AddvsDelivery = lazy(() => import("./AddvsDelivery"));
const DealStages = lazy(() => import("./DealStages"));

const AppRoutes = () => {
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        {/* Public Routes - these do not use the main layout */}
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPasswordForm />} />
        <Route path="/set-new-password/:token" element={<NewPasswordForm />} />
        <Route path="/no-access" element={<NoAuth />} />

        {/* Protected Routes - These are nested inside the AppLayout */}
        <Route element={<AppLayout />}>
          {/* Default route for authenticated users */}
          <Route path="/" element={<Navigate to="/home/dashboard" replace />} />

          {/* Copy and paste all the routes from inside your old HomeScreen.js here */}
          <Route
            path="/home/dashboard"
            element={
              <CheckPermission path="dashboard">
                <Dashboard />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/home/profiles/user"
            element={
              <CheckPermission path="user-profile">
                <UserProfile />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/profile"
            element={
              <CheckPermission path="agency">
                <Profile />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/enquiry"
            element={
              <CheckPermission path="enquiry">
                <Enquiry />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sale/inquiries/enquiry"
            element={
              <CheckPermission path="enquiry">
                <Enquiry workFor="Enquiry" />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/sale/inquiries/editenquiry"
            element={
              <CheckPermission path="enquiry">
                <Enquiry workFor="editEnquiry" />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/sale/inquiries/newenquiry"
            element={
              <CheckPermission path="enquiry">
                <Enquiry workFor="newEnquiry" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sale/inquiries"
            element={
              <CheckPermission path="enquiry">
                <EnquiryList />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sale/enquiries"
            element={
              <CheckPermission path="enquiry">
                <EnquiryBoard />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sale/enquiries/:type"
            element={
              <CheckPermission path="enquiry">
                <EnquiryList />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sale/leads/:type/edit"
            element={
              <CheckPermission path="enquiry">
                <EditEnquiry />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sale/enquiries/:type/edit"
            element={
              <CheckPermission path="enquiry">
                <EditEnquiry />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sale/leads/:type/add"
            element={
              <CheckPermission path="enquiry">
                <EditEnquiry />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sale/enquiries/:type/add"
            element={
              <CheckPermission path="enquiry">
                <EditEnquiry />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sale/enquiries/add"
            element={
              <CheckPermission path="enquiry">
                <EditEnquiry />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sale/data"
            element={
              <CheckPermission path="enquiry">
                <SalesData />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sale/leads"
            element={
              <CheckPermission path="enquiry">
                <LeadsData />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sales/add"
            element={
              <CheckPermission path="enquiry">
                <AddData />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sale/area-Assign"
            element={<AreaAssignListList />}
            exact
          />
          <Route
            path="/sale/area-Assign/add-AsignArea"
            element={
              <CheckPermission path="enquiry">
                <AddAssignArea />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sale/inquiries/sendmessages"
            element={
              <CheckPermission path="enquiry">
                <SendMessages />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/DealStage/dealStages"
            element={
              <CheckPermission path="deal-stages">
                <DealStages />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/DealStage/AddvsDelivery"
            element={
              <CheckPermission path="deal-stages">
                <AddvsDelivery />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/Leads/leadsboard"
            element={
              <CheckPermission path="deal-stages">
                <LeadsBoard />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration"
            element={
              <CheckPermission path="profile">
                <Master />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/state"
            element={
              <CheckPermission path="profile">
                <State_list />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/district"
            element={
              <CheckPermission path="profile">
                <District_list />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/village"
            element={
              <CheckPermission path="profile">
                <Village_list />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/taluka"
            element={
              <CheckPermission path="profile">
                <Taluka_list />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/partlist"
            element={
              <CheckPermission path="profile">
                <Part_List />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/notification"
            element={
              <CheckPermission path="profile">
                <ReportNotification />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/department"
            element={
              <CheckPermission path="profile">
                <Department_list workFor="department" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/department/add"
            element={
              <CheckPermission path="profile">
                <AddDepartment workFor="forAdd" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/department/edit"
            element={
              <CheckPermission path="profile">
                <AddDepartment workFor="forEdit" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/manufacturer"
            element={
              <CheckPermission path="profile">
                <Manufacturer_list />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/manufacture/modal"
            element={
              <CheckPermission path="profile">
                <Manufacturer_modal />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/manufacture/modal/variants"
            element={
              <CheckPermission path="profile">
                <Variants />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/enquirysources"
            element={
              <CheckPermission path="profile">
                <EnquirySources workFor="forAddd" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/holiday"
            element={
              <CheckPermission path="profile">
                <Holiday />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/MessagesList"
            element={
              <CheckPermission path="profile">
                <MessagesList />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/customfield"
            element={
              <CheckPermission path="profile">
                <CustomField />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/MessagesList/Message"
            element={
              <CheckPermission path="profile">
                <Message workFor="addMsg" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/MessagesList/edit"
            element={
              <CheckPermission path="profile">
                <Message workFor="editMsg" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/enquirysourcesmodel"
            element={
              <CheckPermission path="profile">
                <EnquirySources_model />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/tax"
            element={
              <CheckPermission path="profile">
                <Tax />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/report"
            element={
              <CheckPermission path="report">
                <Report />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/report/totalenquiry"
            element={
              <CheckPermission path="report">
                <SalesReport />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sale/leads/:type"
            element={
              <CheckPermission path="enquiry">
                <LeadsRecords />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/sale/data/:type"
            element={
              <CheckPermission path="enquiry">
                <EnquiryStage />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/sale/data/:type/edit"
            element={
              <CheckPermission path="enquiry">
                <EditEnquiry />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="production/order"
            element={
              <CheckPermission path="production">
                <OrderRecord />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/administration/report/WorkAssignArea"
            element={
              <CheckPermission path="report">
                <Work_Assign_Area />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/report/workreport"
            element={
              <CheckPermission path="report">
                <WorkReport />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/report/callreport"
            element={
              <CheckPermission path="report">
                <CallReport />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/report/callreport/incompletecalldetails"
            element={
              <CheckPermission path="report">
                <CallReportInDetails />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/report/trackreport"
            element={
              <CheckPermission path="report">
                <TrackReport />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/report/workreport/details"
            element={
              <CheckPermission path="report">
                <WorkReportDetails />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/service/sales"
            element={
              <CheckPermission path="sales">
                <Sales />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/management/manage"
            element={
              <CheckPermission path="manage">
                <Manage />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/reports"
            element={
              <CheckPermission path="report">
                <ReportMenu />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/reports/task/analytics"
            element={
              <CheckPermission path="report">
                <TaskAnalyticsReport />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/management/manage/myleave"
            element={
              <CheckPermission path="manage">
                <MyLeave />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/management/mytask"
            element={
              <CheckPermission path="manage">
                <Task />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/login"
            element={
              <CheckPermission path="login">
                <Login />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/management/mytask/task-detail"
            element={
              <CheckPermission path="manage">
                <UserTaskDetails />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/administration/*"
            element={<Navigate to="/administration/configuration" />}
            exact
          />
          <Route
            path="/administration/roles"
            element={
              <CheckPermission path="roles">
                <Roles workFor="roles" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/roles/add"
            element={
              <CheckPermission path="add-role">
                <AddRole workFor="addRole" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/roles/edit"
            element={
              <CheckPermission path="add-role">
                <AddRole workFor="forEdit" />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/administration/users"
            element={
              <CheckPermission path="users">
                <Employees />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/administration/users/add"
            element={
              <CheckPermission path="add-user">
                <EmployeeForm workFor="Add" />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/administration/users/edit"
            element={
              <CheckPermission path="edit-user">
                <EmployeeForm workFor="Edit" />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/administration/enquirycategories"
            element={
              <CheckPermission path="category">
                <EnquiryCategories />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/administration/configuration/category"
            element={
              <CheckPermission path="category">
                <Category_list workFor="category" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/category/add"
            element={
              <CheckPermission path="category">
                <AddCategory workFor="forAdd" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/category/edit"
            element={
              <CheckPermission path="category">
                <AddCategory workFor="forEdit" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/branch"
            element={
              <CheckPermission path="branch">
                <Branch workFor="branch" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/branch/add"
            element={
              <CheckPermission path="branch">
                <Branch workFor="addBranch" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/branch/edit"
            element={
              <CheckPermission path="branch">
                <Branch workFor="editBranch" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/task"
            element={
              <CheckPermission path="task">
                <Task workFor="Task" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/task/report"
            element={
              <CheckPermission path="task">
                <TaskCompletionReport />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/task/:type"
            element={
              <CheckPermission path="task">
                <TaskForm />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/home/profile/agency"
            element={
              <CheckPermission path="agency">
                <Profile_list workFor="forEdit" />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/administration/configuration/agency"
            element={
              <CheckPermission path="agency">
                <Profile_list workFor="forAdd" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/addbank"
            element={
              <CheckPermission path="profile">
                <AddBank workFor="forAdd" />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/offer"
            element={
              <CheckPermission path="profile">
                <Offers />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/configlist"
            element={
              <CheckPermission path="profile">
                <ConfigList />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/config"
            element={
              <CheckPermission path="profile">
                <Config />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/offer/:type"
            element={
              <CheckPermission path="profile">
                <AddOffer />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/administration/configuration/config"
            element={
              <CheckPermission path="profile">
                <Config />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/editconfig"
            element={
              <CheckPermission path="profile">
                <EditConfig />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/stocks"
            element={
              <CheckPermission path="inventory">
                <ModelStock />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/stocks/delivery/add"
            element={
              <CheckPermission path="inventory">
                <DeliveryForm />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/stock/billing"
            element={
              <CheckPermission path="inventory">
                <ModelBillings />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/delivery/data"
            element={
              <CheckPermission path="inventory">
                <DeliveriesData />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/delivery/data/add"
            element={
              <CheckPermission path="inventory">
                <AdditionalRecordformWrapper />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/delivery/steps"
            element={
              <CheckPermission path="inventory">
                <DeliveryProcess />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/delivery/steps/:type"
            element={
              <CheckPermission path="inventory">
                <DeliveryStepsData />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/delivery/steps/finalization/edit"
            element={
              <CheckPermission path="inventory">
                <AdditionalForm />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/stock/billing/steps"
            element={
              <CheckPermission path="inventory">
                <DeliveryProcess />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/stock/billing/:type"
            element={
              <CheckPermission path="inventory">
                <AddRecordForm />
              </CheckPermission>
            }
            exact
          />

          <Route
            path="/service/servicing"
            element={
              <CheckPermission path="service">
                <ServicesMenu />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/service/due"
            element={
              <CheckPermission path="service">
                <ServicesDue />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/service/sold"
            element={
              <CheckPermission path="service">
                <TractorSoldOut />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/service/servicing/:type"
            element={
              <CheckPermission path="service">
                <AssociatedServices />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/service/products"
            element={
              <CheckPermission path="service">
                <ProductsList />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/tracking/live-map"
            element={
              <CheckPermission path="service">
                <EmployeeTrackingDashboard />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/service/delivery"
            element={
              <CheckPermission path="service">
                <AllServicesData />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/service/service"
            element={
              <CheckPermission path="service">
                <ServiceRecord />
              </CheckPermission>
            }
            exact
          />
          <Route
            path="/administration/configuration/fields"
            element={
              <CheckPermission path="profile">
                <FieldMapping />
              </CheckPermission>
            }
            exact
          />

          {/* Catch-all for any other authenticated route */}
          <Route path="*" element={<Navigate to="/home/dashboard" />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
